import React, { useMemo } from 'react';
import { calculateUserStats } from '../utils/userStats';
import { calculateStreak } from '../utils/streakCalculator';
import { BellIcon, TrophyIcon, FireIcon, CheckCircleIcon, ClockIcon } from './icons/InterfaceIcons';

const defaultSnapshot = {
  weeklyStreak: 5,
  accuracy: 81,
  totalTime: '23h 45m',
  leaderboardPosition: 14,
};

export const RightRail = ({ user }) => {

  const userStats = useMemo(() => {
    if (user) {
      const stats = calculateUserStats(user.tests?.completed);
      const streak = calculateStreak(user.tests?.completed);
      const completedTests = user.tests?.completed ? Object.values(user.tests.completed) : [];
      
      return {
        weeklyStreak: streak.current,
        accuracy: stats.accuracy,
        totalTime: stats.totalTime,
        leaderboardPosition: defaultSnapshot.leaderboardPosition,
        completedCount: completedTests.length,
        longestStreak: streak.longest
      };
    }
    return { ...defaultSnapshot, completedCount: 0, longestStreak: 0 };
  }, [user]);
  

  return (
    <aside className="hidden xl:block w-80 flex-shrink-0 p-6 border-l border-brand-border overflow-y-auto bg-brand-bg">
      <div className="space-y-8">
        {/* Your Snapshot */}
        <div className="bg-brand-surface border border-brand-border rounded-xl p-5 shadow-card">
          <h3 className="font-bold text-lg mb-4 text-brand-text-primary">Your Snapshot</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="flex items-center justify-center space-x-1">
                  <FireIcon className="w-5 h-5 text-orange-500" />
                  <p className="text-2xl font-bold text-brand-primary">{userStats.weeklyStreak}</p>
                </div>
                <p className="text-xs text-brand-text-secondary">Current Streak</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-secondary">{userStats.accuracy}%</p>
                <p className="text-xs text-brand-text-secondary">Avg. Accuracy</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-accent-amber">{userStats.completedCount}</p>
                <p className="text-xs text-brand-text-secondary">Tests Done</p>
              </div>
              <div>
                <div className="flex items-center justify-center space-x-1">
                  <TrophyIcon className="w-5 h-5 text-yellow-500" />
                  <p className="text-2xl font-bold text-brand-text-primary">{userStats.longestStreak}</p>
                </div>
                <p className="text-xs text-brand-text-secondary">Best Streak</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Recent Achievements */}
        {user && (
          <div className="bg-brand-surface border border-brand-border rounded-xl p-5 shadow-card">
            <h3 className="font-bold text-lg mb-4 text-brand-text-primary">Achievements</h3>
            <div className="space-y-3">
              {userStats.completedCount >= 10 && (
                <div className="flex items-center space-x-3 p-2 bg-brand-bg rounded-lg">
                  <CheckCircleIcon className="w-6 h-6 text-green-500" />
                  <div>
                    <p className="font-semibold text-sm text-brand-text-primary">Consistent Learner</p>
                    <p className="text-xs text-brand-text-secondary">{userStats.completedCount} tests completed</p>
                  </div>
                </div>
              )}
              
              {userStats.weeklyStreak >= 5 && (
                <div className="flex items-center space-x-3 p-2 bg-brand-bg rounded-lg">
                  <FireIcon className="w-6 h-6 text-orange-500" />
                  <div>
                    <p className="font-semibold text-sm text-brand-text-primary">On Fire!</p>
                    <p className="text-xs text-brand-text-secondary">{userStats.weeklyStreak} day streak</p>
                  </div>
                </div>
              )}
              
              {userStats.accuracy >= 80 && (
                <div className="flex items-center space-x-3 p-2 bg-brand-bg rounded-lg">
                  <TrophyIcon className="w-6 h-6 text-yellow-500" />
                  <div>
                    <p className="font-semibold text-sm text-brand-text-primary">High Achiever</p>
                    <p className="text-xs text-brand-text-secondary">{userStats.accuracy}% average accuracy</p>
                  </div>
                </div>
              )}
              
              {userStats.completedCount === 0 && (
                <div className="text-center py-4">
                  <p className="text-sm text-brand-text-secondary">Complete tests to unlock achievements!</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};